import type { Point, BayWindow, TextElement, Compass as CompassType, Wall, Room, RoomCorner } from '../types';
import { GRID_SIZE, GRID_COLOR, WALL_THICKNESS, POINT_SNAP_THRESHOLD, SNAP_THRESHOLD, PIXELS_PER_METER, RESIZE_HANDLE_SIZE, MEASUREMENT_FONT, getZoomResponsiveMeasurementFont, calculateZoomResponsiveFontSize } from '../constants/floorPlanConstants'; // Import necessary constants
import { isNearPoint, findAlignedCorners, getRoomBounds } from './floorPlanUtils'; // Import isNearPoint and findAlignedCorners

// Define WallWithTempId if it's specific to how drawRoomCornerPoints uses walls
// Or ensure currentWalls passed to it from FloorPlanCreator already has tempId if needed.
// For now, assuming Wall type is sufficient for drawRoomCornerPoints as passed.
type WallWithTempId = Wall & { tempId?: string };


export const drawGrid = (
  ctx: CanvasRenderingContext2D,
  viewOffset: Point,
  scale: number,
  canvasWidth: number,
  canvasHeight: number,
  gridSize: number,
  gridColor: string
) => {
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const visWorldXStart = viewOffset.x;
  const visWorldYStart = viewOffset.y;
  const visWorldXEnd = viewOffset.x + canvasWidth / scale;
  const visWorldYEnd = viewOffset.y + canvasHeight / scale;

  const gridStartX = Math.floor(visWorldXStart / gridSize) * gridSize;
  const gridStartY = Math.floor(visWorldYStart / gridSize) * gridSize;
  const gridEndX = Math.ceil(visWorldXEnd / gridSize) * gridSize;
  const gridEndY = Math.ceil(visWorldYEnd / gridSize) * gridSize;

  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 0.5 / scale;

  for (let x = gridStartX; x <= gridEndX; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, gridStartY);
    ctx.lineTo(x, gridEndY);
    ctx.stroke();
  }

  for (let y = gridStartY; y <= gridEndY; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(gridStartX, y);
    ctx.lineTo(gridEndX, y);
    ctx.stroke();
  }

  // Major grid lines
  ctx.strokeStyle = '#D1D5DB'; // Consider making this a passed constant
  ctx.lineWidth = 1 / scale;

  for (let x = gridStartX; x <= gridEndX; x += gridSize * 5) {
    if (x >= visWorldXStart && x <= visWorldXEnd) {
      ctx.beginPath();
      ctx.moveTo(x, gridStartY);
      ctx.lineTo(x, gridEndY);
      ctx.stroke();
    }
  }

  for (let y = gridStartY; y <= gridEndY; y += gridSize * 5) {
    if (y >= visWorldYStart && y <= visWorldYEnd) {
      ctx.beginPath();
      ctx.moveTo(gridStartX, y);
      ctx.lineTo(gridEndX, y);
      ctx.stroke();
    }
  }
  ctx.restore();
};

export const drawRoomCornerPoints = (ctx: CanvasRenderingContext2D, corners: Point[], scale: number, hoveredCorner: Point | null, viewOffset: Point) => {
  const cornerPoints = new Set<string>();
  
  corners.forEach(corner => {
    cornerPoints.add(`${corner.x},${corner.y}`);
  });

  ctx.save();
  corners.forEach(corner => {
    const isHovered = hoveredCorner && isNearPoint(hoveredCorner, corner, SNAP_THRESHOLD * 10 / scale);
    
    ctx.beginPath();
    // EXTRA LARGE visual indicators for easy corner selection
    ctx.arc(corner.x, corner.y, (isHovered ? 16 : 12) / scale, 0, Math.PI * 2);
    ctx.fillStyle = isHovered ? '#FF8C00' : '#2196F3';
    ctx.fill();
    // Always add a contrasting stroke for better visibility
    ctx.strokeStyle = isHovered ? '#FFFFFF' : '#FFFFFF';
    ctx.lineWidth = (isHovered ? 2.5 : 2) / scale;
    ctx.stroke();
    ctx.closePath();
  });
  ctx.restore();
};

export const drawEraserPreview = (ctx: CanvasRenderingContext2D, point: Point, scale: number) => {
  // Enhanced eraser preview for touch devices - MATCH ACTUAL ERASER TOLERANCE
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Use the same tolerance calculation as eraseAtPoint function
  const baseEraserTolerance = isTouchDevice ? SNAP_THRESHOLD * 2.5 : SNAP_THRESHOLD;
  const radius = baseEraserTolerance / scale; // Match actual eraser tolerance
  
  ctx.save();
  
  // Draw outer circle (touch area indicator for touch devices)
  if (isTouchDevice) {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius * 1.2, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.2)';
    ctx.lineWidth = 1 / scale;
    ctx.stroke();
    ctx.fillStyle = 'rgba(255, 0, 0, 0.05)';
    ctx.fill();
  }
  
  // Draw main eraser circle - now matches actual tolerance
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = isTouchDevice ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 0, 0, 0.5)';
  ctx.lineWidth = isTouchDevice ? 3 / scale : 2 / scale;
  ctx.stroke();
  
  // Fill with semi-transparent red
  ctx.fillStyle = isTouchDevice ? 'rgba(255, 0, 0, 0.15)' : 'rgba(255, 0, 0, 0.1)';
  ctx.fill();
  
  // Add crosshair for precision on touch devices
  if (isTouchDevice) {
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.6)';
    ctx.lineWidth = 1 / scale;
    const crossSize = 5 / scale;
    
    // Horizontal line
    ctx.beginPath();
    ctx.moveTo(point.x - crossSize, point.y);
    ctx.lineTo(point.x + crossSize, point.y);
    ctx.stroke();
    
    // Vertical line
    ctx.beginPath();
    ctx.moveTo(point.x, point.y - crossSize);
    ctx.lineTo(point.x, point.y + crossSize);
    ctx.stroke();
  }
  
  ctx.restore();
};

export const drawBayWindow = (ctx: CanvasRenderingContext2D, bayWindow: BayWindow) => {
  const { position, width, depth, style } = bayWindow;
  
  ctx.save();
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 2;

  if (style === 'canted') {
    ctx.beginPath();
    ctx.moveTo(position.x - width/2, position.y);
    ctx.lineTo(position.x - width/4, position.y + depth);
    ctx.lineTo(position.x + width/4, position.y + depth);
    ctx.lineTo(position.x + width/2, position.y);
    ctx.stroke();
  } else if (style === 'square') {
    ctx.strokeRect(
      position.x - width/2,
      position.y,
      width,
      depth
    );
  } else { // shallow
    ctx.beginPath();
    ctx.moveTo(position.x - width/2, position.y);
    ctx.lineTo(position.x, position.y + depth);
    ctx.lineTo(position.x + width/2, position.y);
    ctx.stroke();
  }
  ctx.restore();
};

export const drawText = (ctx: CanvasRenderingContext2D, textElement: TextElement, scale: number) => {
  const { position, content, fontSize, rotation } = textElement;
  
  ctx.save();
  ctx.translate(position.x, position.y);
  ctx.rotate(rotation * Math.PI / 180);
  
  const responsiveFont = getZoomResponsiveMeasurementFont(scale);
  ctx.font = responsiveFont;
  ctx.fillStyle = '#000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(content, 0, 0);
  
  ctx.restore();
};

export const drawCompass = (ctx: CanvasRenderingContext2D, compass: CompassType, scale: number = 1, isActive: boolean = false) => {
  const { position, size, rotation } = compass;
  
  ctx.save();
  ctx.translate(position.x, position.y);
  ctx.rotate(rotation * Math.PI / 180);
  
  // Modern shadow for depth
  ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  
  // Draw outer circle with modern gradient
  const outerGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, size);
  outerGradient.addColorStop(0, '#ffffff');
  outerGradient.addColorStop(0.6, '#f8fafc');
  outerGradient.addColorStop(1, '#e2e8f0');
  
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fillStyle = outerGradient;
  ctx.fill();
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 2.5 / scale;
  ctx.stroke();
  
  // Reset shadow for inner elements
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // Draw decorative inner circles
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.88, 0, Math.PI * 2);
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1 / scale;
  ctx.stroke();
  
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.75, 0, Math.PI * 2);
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 0.5 / scale;
  ctx.stroke();
  
  // Draw cardinal direction markers (subtle tick marks)
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.5 / scale;
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI / 2);
    ctx.beginPath();
    ctx.moveTo(Math.cos(angle) * size * 0.88, Math.sin(angle) * size * 0.88);
    ctx.lineTo(Math.cos(angle) * size * 0.95, Math.sin(angle) * size * 0.95);
    ctx.stroke();
  }
  
  // Draw North arrow (modern, bold red)
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.7);
  ctx.lineTo(-size * 0.18, -size * 0.15);
  ctx.lineTo(0, -size * 0.28);
  ctx.lineTo(size * 0.18, -size * 0.15);
  ctx.closePath();
  const northGradient = ctx.createLinearGradient(0, -size * 0.7, 0, -size * 0.15);
  northGradient.addColorStop(0, '#dc2626');
  northGradient.addColorStop(1, '#ef4444');
  ctx.fillStyle = northGradient;
  ctx.fill();
  ctx.strokeStyle = '#991b1b';
  ctx.lineWidth = 1.8 / scale;
  ctx.stroke();
  
  // Draw South arrow (modern, white with shadow)
  ctx.beginPath();
  ctx.moveTo(0, size * 0.7);
  ctx.lineTo(-size * 0.18, size * 0.15);
  ctx.lineTo(0, size * 0.28);
  ctx.lineTo(size * 0.18, size * 0.15);
  ctx.closePath();
  const southGradient = ctx.createLinearGradient(0, size * 0.15, 0, size * 0.7);
  southGradient.addColorStop(0, '#ffffff');
  southGradient.addColorStop(1, '#f1f5f9');
  ctx.fillStyle = southGradient;
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.8 / scale;
  ctx.stroke();
  
  // Draw East and West arrows (smaller, subtle)
  // East arrow
  ctx.beginPath();
  ctx.moveTo(size * 0.65, 0);
  ctx.lineTo(size * 0.2, -size * 0.12);
  ctx.lineTo(size * 0.3, 0);
  ctx.lineTo(size * 0.2, size * 0.12);
  ctx.closePath();
  ctx.fillStyle = '#94a3b8';
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.2 / scale;
  ctx.stroke();
  
  // West arrow
  ctx.beginPath();
  ctx.moveTo(-size * 0.65, 0);
  ctx.lineTo(-size * 0.2, -size * 0.12);
  ctx.lineTo(-size * 0.3, 0);
  ctx.lineTo(-size * 0.2, size * 0.12);
  ctx.closePath();
  ctx.fillStyle = '#94a3b8';
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.2 / scale;
  ctx.stroke();
  
  // Use zoom-responsive font size for compass labels
  const responsiveFontSize = calculateZoomResponsiveFontSize(13, scale);
  ctx.font = `bold ${responsiveFontSize}px 'Inter', 'SF Pro Display', -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  // North label (white on red)
  ctx.fillStyle = '#ffffff';
  ctx.fillText('N', 0, -size * 0.45);
  
  // South label
  ctx.fillStyle = '#475569';
  ctx.fillText('S', 0, size * 0.45);
  
  // East label
  const eastFontSize = calculateZoomResponsiveFontSize(11, scale);
  ctx.font = `600 ${eastFontSize}px 'Inter', 'SF Pro Display', -apple-system, sans-serif`;
  ctx.fillStyle = '#64748b';
  ctx.fillText('E', size * 0.82, 0);
  
  // West label
  ctx.fillStyle = '#64748b';
  ctx.fillText('W', -size * 0.82, 0);
  
  // Draw modern center dot with ring
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.1, 0, Math.PI * 2);
  ctx.fillStyle = '#1e293b';
  ctx.fill();
  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 1.5 / scale;
  ctx.stroke();
  
  // Draw rotation control handle (visible when compass tool is active)
  if (isActive) {
    ctx.beginPath();
    ctx.arc(size * 1.3, 0, size * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = '#10b981';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5 / scale;
    ctx.stroke();
    
    // Add rotation icon (circular arrow)
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2 / scale;
    const rotSize = size * 0.18;
    ctx.beginPath();
    ctx.arc(size * 1.3, 0, rotSize, -Math.PI / 2, Math.PI, false);
    ctx.stroke();
    // Arrow head
    ctx.beginPath();
    ctx.moveTo(size * 1.3 - rotSize, 0);
    ctx.lineTo(size * 1.3 - rotSize - 4 / scale, -4 / scale);
    ctx.lineTo(size * 1.3 - rotSize + 4 / scale, -4 / scale);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  }

  ctx.restore();
};

export const drawRoomPreview = (
  ctx: CanvasRenderingContext2D,
  start: Point,
  end: Point,
  pixelsPerMeter: number,
  measurementFont: string
) => {
  // Intentionally left blank to prevent drawing the room preview
  return;
};

export const drawConnectionPoints = (
  ctx: CanvasRenderingContext2D,
  walls: Wall[],
  snapThreshold: number,
  hoverPoint: Point | null = null
) => {
  walls.forEach(wall => {
    [wall.start, wall.end].forEach(point => {
      const isHovered = hoverPoint && Math.hypot(point.x - hoverPoint.x, point.y - hoverPoint.y) < snapThreshold;
      ctx.beginPath();
      ctx.arc(point.x, point.y, 5, 0, Math.PI * 2); // Consider making radius a constant
      ctx.fillStyle = isHovered ? '#FF8C00' : '#2196F3';
      ctx.fill();
      ctx.closePath();
    });
  });
};

export const drawGuideLines = (
  ctx: CanvasRenderingContext2D,
  point: Point,
  walls: Wall[],
  snapThreshold: number // Pass snapThreshold if used by findAlignedCorners or for drawing
) => {
  if (!point) return;
  ctx.save();
  // Assuming findAlignedCorners from floorPlanUtils correctly uses its own SNAP_THRESHOLD or one passed to it.
  // The original drawGuideLines in FloorPlanCreator didn't explicitly use SNAP_THRESHOLD for drawing lines,
  // but rather for the findAlignedCorners logic.
  const { vertical, horizontal } = findAlignedCorners(point, walls); // findAlignedCorners uses its internal SNAP_THRESHOLD

  ctx.strokeStyle = 'rgba(0,0,255,0.5)'; // Blue, semi-transparent
  ctx.lineWidth = 1; // Consider scaling with canvas scale or make constant
  ctx.setLineDash([2, 2]);

  vertical.forEach(vp => {
    ctx.beginPath();
    ctx.moveTo(vp.x, Math.min(point.y, vp.y));
    ctx.lineTo(vp.x, Math.max(point.y, vp.y));
    ctx.stroke();
  });

  horizontal.forEach(hp => {
    ctx.beginPath();
    ctx.moveTo(Math.min(point.x, hp.x), hp.y);
    ctx.lineTo(Math.max(point.x, hp.x), hp.y);
    ctx.stroke();
  });
  ctx.restore();
};

export const drawTouchFeedback = (ctx: CanvasRenderingContext2D, point: Point) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(point.x, point.y, 20, 0, Math.PI * 2); // Radius as constant?
  ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(point.x, point.y, 4, 0, Math.PI * 2); // Radius as constant?
  ctx.fillStyle = '#3B82F6';
  ctx.fill();
  ctx.restore();
};

export const drawResizeHandles = (
  ctx: CanvasRenderingContext2D,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  isSelected: boolean,
  handleSize: number, // Pass RESIZE_HANDLE_SIZE
  hoveredHandleId: string | null
) => {
  const { minX, maxX, minY, maxY } = bounds;
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;

  const handles = [
    { id: 'nw', center: { x: minX, y: minY } }, { id: 'n', center: { x: midX, y: minY } },
    { id: 'ne', center: { x: maxX, y: minY } }, { id: 'w', center: { x: minX, y: midY } },
    { id: 'e', center: { x: maxX, y: midY } }, { id: 'sw', center: { x: minX, y: maxY } },
    { id: 's', center: { x: midX, y: maxY } }, { id: 'se', center: { x: maxX, y: maxY } }
  ];

  ctx.save();
  const transform = ctx.getTransform(); // Get current world-to-screen transform
  ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to screen space for drawing handles

  handles.forEach(handle => {
    // Transform world coordinates of handle center to screen coordinates
    const screenX = handle.center.x * transform.a + transform.e;
    const screenY = handle.center.y * transform.d + transform.f; // Assuming no skew (b,c are 0)

    ctx.beginPath();
    ctx.arc(screenX, screenY, handleSize, 0, Math.PI * 2);
    ctx.fillStyle = hoveredHandleId === handle.id
      ? (isSelected ? '#1565C0' : '#64B5F6')
      : (isSelected ? '#2196F3' : '#90CAF9');
    ctx.fill();
    // Optional: ctx.stroke() if you want borders on handles
  });
  ctx.restore(); // Restore original world transform
};

// You can add other drawing helpers here like:
// export const drawConnectionPoints = (...) => { ... };
// export const drawGuideLines = (...) => { ... };
// export const drawTouchFeedback = (...) => { ... };
